<template>
  <div class="activity-stage puzzling-letters">
    <div class="puzzling-letters-container">
      <div class="puzzling-letters-title">
        <h2>{{ currentStep.imageNoExtension }}</h2>
      </div>

      <div class="background-image-container">
        <img class="background-image" :src="currentStep.image" />
      </div>

      <div class="puzzle-letters-container">
        <div class="puzzle-piece-container absolute-puzzle-01">
          <svg height="220px" class="puzzlepiece01" v-html="puzzlepiece01"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>

        <div class="puzzle-piece-container absolute-puzzle-02">
          <svg height="179px" class="puzzlepiece02" v-html="puzzlepiece02"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>

        <div class="puzzle-piece-container absolute-puzzle-03">
          <svg height="220px" class="puzzlepiece03" v-html="puzzlepiece03"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>

        <div class="puzzle-piece-container absolute-puzzle-04">
          <svg height="188px" class="puzzlepiece04" v-html="puzzlepiece04"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>

        <div class="puzzle-piece-container absolute-puzzle-05">
          <svg height="235px" class="puzzlepiece05" v-html="puzzlepiece05"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>

        <div class="puzzle-piece-container absolute-puzzle-06">
          <svg height="154px" class="puzzlepiece06" v-html="puzzlepiece06"></svg>

          <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

          <div class="puzzle-piece">
            <p>{{ currentStep.answer }}</p>
          </div>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import { shuffle, range } from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import puzzlepiece01 from 'assets/svg/puzzling-letters/puzzle-piece-01.svg';
import puzzlepiece02 from 'assets/svg/puzzling-letters/puzzle-piece-02.svg';
import puzzlepiece03 from 'assets/svg/puzzling-letters/puzzle-piece-03.svg';
import puzzlepiece04 from 'assets/svg/puzzling-letters/puzzle-piece-04.svg';
import puzzlepiece05 from 'assets/svg/puzzling-letters/puzzle-piece-05.svg';
import puzzlepiece06 from 'assets/svg/puzzling-letters/puzzle-piece-06.svg';
import puzzlingoutline from 'assets/svg/puzzling-letters/puzzling-letter-outline.svg';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'ObjectShuffle',
      sfx: null,
      puzzlepiece01,
      puzzlepiece02,
      puzzlepiece03,
      puzzlepiece04,
      puzzlepiece05,
      puzzlepiece06,
      puzzlingoutline,
      puzzlePieces: []
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      const [{ steps }] = roundSetup.rounds;
      await this.loadImages(steps);
      this.sfx = await this.loadSounds(steps);
    },

    async loadImages(steps) {
      steps.forEach(({ image }) => this.queueImage(image));
      return this.downloadImages();
    },

    async loadSounds(steps) {
      const soundPaths = steps.map(({ audio }) => audio);
      this.queueAudio(soundPaths);

      const cache = await this.downloadAudio();
      return steps.map(({ audio }) => cache.get(audio));
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;
      const written = characters ? 'Hold the target. Type each letter.' : 'Type each letter.';
      const voice = characters
        ? `${this.voiceInstructionsPrefix}/puzzlesurprise-${this.getRandomVoice()}-1.mp3`
        : `${this.voiceInstructionsPrefix}/shuffledlettersnth-${this.getRandomVoice()}-1.mp3`;

      this.puzzlePieces = this.generateRandomPieceArray();

      return {
        rounds,
        instructions: {
          written,
          voice,
          delay: 4
        }
      };
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    // generate a random array used to hide the puzzle pieces
    generateRandomPieceArray() {
      return shuffle(range(0, 6));
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.to('.puzzling-letters-title', 0, { css: { autoAlpha: 0 } });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.hidePuzzlePiece();
      if (this.puzzlePieces.length === 0) {
        this.disableKeyboard();
        this.handleStepEnd();
      }
    },

    hidePuzzlePiece() {
      const allPieces = document.getElementsByClassName('puzzle-piece-container');
      const index = this.puzzlePieces[0];
      TweenMax.to(allPieces[index], 0.25, { css: { autoAlpha: 0 } });
      this.puzzlePieces = this.puzzlePieces.slice(1);
    },

    handleStepEnd() {
      const { stepIndex, audio } = this.currentStep;
      this.sndBell();
      TweenMax.to('.puzzling-letters-title', 0.5, { css: { autoAlpha: 1 } });
      if (audio) {
        this.sfx[stepIndex].play();
        TweenMax.delayedCall(2, this.finishStep);
      } else {
        TweenMax.delayedCall(0.5, this.finishStep);
      }
    },

    finishStep() {
      if (this.isLastStepInRound) {
        this.handleRoundEnd();
      } else {
        TweenMax.to(document.getElementsByClassName('puzzle-piece-container'), 0, {
          css: { autoAlpha: 1 }
        });
        TweenMax.to('.puzzling-letters-title', 0, { css: { autoAlpha: 0 } });
        this.completeStep();
        this.setCurrentStep();
        this.enableKeyboard();
        this.puzzlePieces = this.generateRandomPieceArray();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(0.5, this.completeActivity);
    }
  }
};
</script>
